/**
 * Contrat de la couche API « Demande d'accompagnement ».
 *
 * Le store Pinia ne dépend QUE de ce module : il importe `accompagnementApi`
 * (l'implémentation active) et les types. Aujourd'hui l'implémentation est le
 * mock localStorage ; le jour où boostcore expose les endpoints, on écrit un
 * `accompagnementHttpApi.ts` (via `apiFetch`) respectant `AccompApi` et on
 * change uniquement la ligne d'export ci-dessous — UI et store inchangés.
 */
import type {
  AccompActionInput,
  AccompActor,
  AccompCreateInput,
  AccompListFilters,
  AccompListResponse,
  AccompOutcome,
  AccompQuotePick,
  AccompRequest,
  AccompStats,
  AccompStatsPeriod,
  AccompTechnician,
  AccompUpdateInput,
  AccompUserPick,
} from './accompagnementTypes'
import { accompagnementHttpApi } from './accompagnementHttpApi'

export interface AccompApi {
  list(filters: AccompListFilters): Promise<AccompListResponse>
  get(id: string): Promise<AccompRequest>
  /** Toutes les demandes d'un client (tous statuts) — pour la fiche client. */
  listByClient(clientUuid: string): Promise<AccompRequest[]>
  create(input: AccompCreateInput, actor: AccompActor): Promise<AccompRequest>
  /** Édition des champs métier d'une demande (le workflow reste géré à part). */
  update(id: string, patch: AccompUpdateInput, actor: AccompActor): Promise<AccompRequest>
  /** Attribution ET réattribution (le statut/horodatage diffèrent selon que c'est la 1ʳᵉ). */
  assign(id: string, technicianUuid: string, actor: AccompActor, note?: string): Promise<AccompRequest>
  /** Prise en charge (acceptation) par le technicien assigné. */
  takeCharge(id: string, actor: AccompActor): Promise<AccompRequest>
  /** Refus par le technicien assigné : renvoie la demande au dispatcher (motif obligatoire). */
  refuse(id: string, note: string, actor: AccompActor): Promise<AccompRequest>
  /** Relance / redispatch d'une demande attribuée non prise en charge (dispatcher). */
  remind(id: string, note: string, actor: AccompActor): Promise<AccompRequest>
  addComment(id: string, message: string, actor: AccompActor): Promise<AccompRequest>
  /** Ajoute une action de suivi (acteurs + date). */
  addAction(id: string, input: AccompActionInput, actor: AccompActor): Promise<AccompRequest>
  deleteAction(id: string, actionId: string): Promise<AccompRequest>
  /** Devis de la company candidats au rattachement (fenêtre autour de l'ouverture). */
  listQuotes(id: string, from?: string, to?: string): Promise<AccompQuotePick[]>
  /** Rattache un devis (Quote) à la demande. */
  attachQuote(id: string, quoteUuid: string): Promise<AccompRequest>
  /** Détache un devis rattaché (par id de lien). */
  detachQuote(id: string, linkId: string): Promise<AccompRequest>
  close(id: string, outcome: AccompOutcome, actor: AccompActor, note?: string): Promise<AccompRequest>
  cancel(id: string, actor: AccompActor, note?: string): Promise<AccompRequest>
  /** Liste des techniciens assignables (= utilisateurs ACCOMP_WRITE côté vrai back). */
  listTechnicians(): Promise<AccompTechnician[]>
  /** Recherche d'utilisateurs internes (intervenants d'action) — gardé par ACCOMP_READ. */
  searchUsers(q: string, signal?: AbortSignal): Promise<AccompUserPick[]>
  stats(period: AccompStatsPeriod): Promise<AccompStats>
}

/** Implémentation active : le backend boostcore (`/accompagnement`) via `apiFetch`. */
export const accompagnementApi: AccompApi = accompagnementHttpApi

export type {
  AccompActionInput,
  AccompActor,
  AccompCreateInput,
  AccompListFilters,
  AccompListResponse,
  AccompRequest,
  AccompStats,
  AccompStatsPeriod,
  AccompTechnician,
  AccompUpdateInput,
  AccompUserPick,
} from './accompagnementTypes'
